import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function ProtectedRoute({ children }) {
  const { user, isAuthenticated, loading, hasWorkspace, workspace } = useAuth();
  const location = useLocation();

  // If still loading, show loading spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Not authenticated -> redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Must change password -> redirect to change-password page
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  // Platform Admin (role === 'admin') is exempt from the workspace gate:
  // it can operate cross-workspace and does not need a workspace of its own.
  const isPlatformAdmin = user?.role === 'admin';

  // Authenticated but no workspace -> redirect to onboarding (except Platform Admin)
  if (!hasWorkspace && !isPlatformAdmin && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Has workspace but trying to access onboarding -> redirect to dashboard
  if (hasWorkspace && location.pathname === '/onboarding') {
    return <Navigate to="/dashboard" replace />;
  }

  // PATCH D: paywall — require an active/trialing/legacy subscription before
  // unlocking the dashboard. Platform admins are exempt (no workspace).
  // The /billing/required and /billing/* paths must NEVER be paywalled —
  // otherwise the user could never resume Stripe Checkout.
  const subscriptionStatus = (workspace?.subscription_status || user?.subscription_status || '').toLowerCase();
  const PAID_STATUSES = ['active', 'trialing', 'legacy_active'];
  const isPaid = PAID_STATUSES.includes(subscriptionStatus);
  const isBillingPath = location.pathname.startsWith('/billing');
  if (hasWorkspace && !isPlatformAdmin && !isPaid && !isBillingPath) {
    return <Navigate to="/billing/required" replace />;
  }

  return children;
}
